/**
 * ExplanationScreen.tsx - Explanation screen for the Endowment experiment
 * - Renders the hint as its own section directly under the "Unser Experiment" card.
 * - Texts are read from translations/experiments/EndowmentEffect.ts (no inline text).
 */

import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@blueprintjs/core";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors } from "../../../../components/colors";
import ExplainImg from "./assets/Cup.png";

import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";
import { EndowmentEffect } from "../../../../translations/experiments/EndowmentEffect";

// --- Helper: get glossary for current language ---
function useGlossaryTerms(language: string) {
    if (language === "de") return deTranslations.glossary?.terms || {};
    if (language === "en") return enTranslations.glossary?.terms || {};
    return {};
}

// --- resolve alias chain in glossary ---
function resolveGlossarEntry(glossar: Record<string, any>, key: string): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

// Tooltip for glossary terms
const BegriffTooltip: React.FC<{ begriff: string; children: React.ReactNode; glossar: Record<string, any> }> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setVisible(false);
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: colors.lichtBlauLighter,
                color: colors.brillantBlue,
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => { setVisible(v => !v); e.stopPropagation(); }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 300,
                            maxWidth: 600,
                            background: colors.white,
                            color: colors.textDark,
                            border: `1.5px solid ${colors.lichtBlauLight}`,
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "18px 20px",
                            fontWeight: 400,
                            fontSize: 15,
                            lineHeight: 1.5,
                            pointerEvents: "auto",
                            whiteSpace: "pre-line",
                            marginTop: 12,
                            textAlign: "justify"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

// inject glossary tooltips for first occurrences (case-insensitive)
function parseWithGlossarTooltip(text: string, terms: string[], used: Set<string>, glossar: Record<string, any>) {
    if (typeof text !== "string") return text;
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => (glossaryKeyMap[k.toLowerCase()] = k));
    if (terms.length === 0) return text;

    const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(`\\b(${escaped.join("|")})\\b`, "gi");
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index));
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;
        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(<BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>{matched}</BegriffTooltip>);
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts;
}

/* -------------------------
   Component
   ------------------------- */
function ExplanationScreenComponent(props: ScreenProps) {
    const { language } = useLanguage();
    const glossar = useGlossaryTerms(language);
    const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
    const used = new Set<string>();

    // read explanation content from translations file (no inline text here)
    const endo = EndowmentEffect[language]?.screens?.explanation || {};
    const data = {
        headline: endo.headline || (language === "de" ? "Was ist der Besitztumseffekt?" : "What is the endowment effect?"),
        imageAlt: endo.imageAlt || (language === "de" ? "Illustration Besitztumseffekt" : "Endowment effect illustration"),
        sections: endo.sections || [],
        experimentNoteTitle: endo.experimentNoteTitle || (language === "de" ? "Unser Experiment" : "Our experiment"),
        experimentNote: endo.experimentNote || "",
        hint: endo.hint || "",
        sourcesTitle: endo.sourcesTitle || (language === "de" ? "Quellen" : "Sources"),
        sources: endo.sources || []
    };

    useEffect(() => {
        props.setCanContinue(true);
        // eslint-disable-next-line
    }, [props]);

    // Build parsed content like Ultimatum example (image floats right in first section)
    const parsedContent: React.ReactNode[] = [];
    for (let i = 0; i < data.sections.length; i++) {
        const sec = data.sections[i];
        parsedContent.push(
            <div key={"section-title-" + i} style={{ marginTop: i === 0 ? 0 : 30, marginBottom: 8 }}>
                <b style={{ fontSize: 20, color: colors.brillantBlue, fontWeight: 600 }}>{sec.title}</b>
            </div>
        );

        if (i === 0) {
            parsedContent.push(
                <img
                    key="explain-img"
                    src={ExplainImg}
                    alt={data.imageAlt}
                    style={{
                        float: "right",
                        marginLeft: 44,
                        marginBottom: 24,
                        width: 220,
                        maxWidth: "43%",
                        borderRadius: 16,
                        boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                        objectFit: "cover"
                    }}
                />
            );
        }

        parsedContent.push(
            <p key={`p-${i}`} style={{
                marginTop: 0,
                marginBottom: 18,
                whiteSpace: "pre-line",
                color: colors.textMedium,
                fontSize: 17,
                lineHeight: 1.7,
                textAlign: "justify",
                textJustify: "inter-word"
            }}>
                {parseWithGlossarTooltip(sec.paragraph || "", terms, used, glossar)}
            </p>
        );
    }

    return (
        <div className="experiment-container" style={{ maxWidth: 1250, margin: "0 auto", padding: "24px 20px 44px", backgroundColor: colors.backgroundLight }}>
            <div style={{ background: colors.white, borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.09)", padding: "24px 40px 36px", marginBottom: 28 }}>
                <h2 style={{ color: colors.brillantBlue, margin: "0 0 24px 0", fontSize: 28, fontWeight: 700, textAlign: "center" }}>
                    {data.headline}
                </h2>

                <div style={{ fontSize: 17, lineHeight: 1.7, position: "relative", minHeight: 320 }}>
                    {parsedContent}
                </div>

                <div style={{
                    marginTop: 38,
                    marginBottom: 12,
                    background: colors.backgroundLight,
                    borderRadius: 11,
                    padding: "26px 32px",
                    fontSize: 17,
                    lineHeight: 1.7,
                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                    color: colors.textMedium,
                    textAlign: "justify",
                    textJustify: "inter-word"
                }}>
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
                        <Icon icon="lab-test" size={20} style={{ marginRight: 12, marginTop: 2, color: colors.brillantBlue }} />
                        <span style={{ fontWeight: 600, fontSize: 18, color: colors.brillantBlue }}>{data.experimentNoteTitle}</span>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: data.experimentNote }} />
                </div>

                {/* NEW: hint as its own section directly under the experiment card */}
                {data.hint ? (
                    <div style={{
                        marginTop: 12,
                        marginBottom: 24,
                        background: colors.white,
                        borderRadius: 11,
                        padding: "18px 22px",
                        fontSize: 16.5,
                        lineHeight: 1.6,
                        boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
                        color: colors.textMedium
                    }}>
                        <b style={{ color: colors.brillantBlue, fontSize: 16 }}>{language === "de" ? "Wichtiger Hinweis" : "Important note"}</b>
                        <div style={{ marginTop: 10 }}>
                            {data.hint}
                        </div>
                    </div>
                ) : null}

                <div style={{ background: colors.backgroundLight, borderRadius: 8, padding: "22px 24px", marginTop: 24, fontSize: 15.5, color: colors.textMedium }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                        <Icon icon="book" size={16} style={{ marginRight: 10, color: colors.brillantBlue }} />
                        <span style={{ fontWeight: 600, color: colors.brillantBlue }}>{data.sourcesTitle}</span>
                    </div>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 24, textAlign: "justify", textJustify: "inter-word" }}>
                        {data.sources.map((src: string, idx: number) => <li key={idx} style={{ marginBottom: idx < data.sources.length - 1 ? 8 : 0 }}>{src}</li>)}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// Exports: component alias + ScreenConfig for compatibility with different import patterns
export const ExplanationScreen: React.FC<any> = (props: any) => <ExplanationScreenComponent {...props} />;
export const ExplanationScreenConfig: ScreenConfig = {
    component: ExplanationScreenComponent,
    defaultResult: {},
    canContinueDefault: true
};